"use client";

// src/ui/components/calculator/picker-modal.tsx
//
// Peptide picker modal (handoff 02 §1.3 / 03 §Modal): bottom-sheet on mobile,
// centered dialog on desktop. Focus-trap + Esc + scrim-close. Renders a 2/3-col
// grid of peptide cards with IN-STOCK peptides FIRST, a name/category search
// filter, and badges (BLEND for "Stack", "Auf Lager" for in-stock, "nur Rechner"
// otherwise). Selecting a peptide calls onPick + closes.
//
// "In stock" is sourced from `inStockSlugs` (REAL Saleor stock, quantityAvailable > 0,
// resolved by the hub) — NOT the static hasBergdorfProduct flag. When no set is given
// (e.g. spoke, where the picker is locked anyway) NO in-stock dot/badge is shown, so
// we never over-claim. semaglutid + out-of-stock peptides are absent from the set, so
// they never show the dot and never read as "Auf Lager".

import { useMemo, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { Search, X } from "lucide-react";
import {
	CUSTOM_PEPTIDE_SLUG,
	PEPTIDE_DATA,
	getPeptideName,
	getPeptideCategory,
} from "@/lib/calculator/peptide-data";
import type { PeptideData } from "@/lib/calculator/types";
import type { Locale } from "@/config/locale";
import { ToolDialog } from "./tool-dialog";

function isBlend(peptide: PeptideData): boolean {
	// Blends are category "Stack" (also reflected by a components[] breakdown).
	return peptide.category.en === "Stack" || !!peptide.components?.length;
}

interface PickerModalProps {
	open: boolean;
	onClose: () => void;
	onPick: (slug: string) => void;
	currentSlug: string;
	locale: string;
	/** Authoritative set of slugs actually IN STOCK now (real Saleor stock); no fallback. */
	inStockSlugs?: ReadonlySet<string>;
}

/** Search state resets on dismissal, including a rapid reopen during the exit phase. */
function ModalContent({ open, onClose, onPick, currentSlug, locale, inStockSlugs }: PickerModalProps) {
	const t = useTranslations("peptideCalculator");
	const [search, setSearch] = useState("");
	const loc = locale as Locale;
	const searchRef = useRef<HTMLInputElement>(null);

	// Real-stock predicate. NO fallback to hasBergdorfProduct: without a set, nothing
	// reads as in stock (so we never over-claim "Auf Lager").
	const isInStock = useMemo(() => {
		return (p: PeptideData): boolean => (inStockSlugs ? inStockSlugs.has(p.slug) : false);
	}, [inStockSlugs]);

	// Catalog without the generic "custom" entry; IN-STOCK peptides FIRST,
	// then by current display name (stable, locale-aware).
	const sorted = useMemo(() => {
		const catalog = PEPTIDE_DATA.filter((p) => p.slug !== CUSTOM_PEPTIDE_SLUG);
		return [...catalog].sort((a, b) => {
			const stockDelta = Number(isInStock(b)) - Number(isInStock(a));
			if (stockDelta !== 0) return stockDelta;
			return getPeptideName(a, loc).localeCompare(getPeptideName(b, loc), loc);
		});
	}, [isInStock, loc]);

	const filtered = useMemo(() => {
		const q = search.trim().toLowerCase();
		if (!q) return sorted;
		return sorted.filter((p) => {
			const name = getPeptideName(p, loc).toLowerCase();
			const cat = getPeptideCategory(p, loc).toLowerCase();
			return name.includes(q) || cat.includes(q);
		});
	}, [search, sorted, loc]);

	const close = () => {
		setSearch("");
		onClose();
	};

	return (
		<ToolDialog
			open={open}
			onClose={close}
			title={t("picker.title")}
			className="sm:max-w-2xl"
			initialFocusRef={searchRef}
		>
			{/* Head */}
			<div className="flex items-center justify-between border-b border-border px-4 py-3 sm:px-5">
				<h3 className="text-base font-semibold text-foreground">{t("picker.title")}</h3>
				<button
					type="button"
					onClick={close}
					aria-label={t("picker.close")}
					className="inline-flex h-9 w-9 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
				>
					<X className="h-5 w-5" />
				</button>
			</div>

			{/* Search */}
			<div className="border-b border-border px-4 py-2.5 sm:px-5">
				<div className="flex items-center gap-2 rounded-md border border-input bg-background px-3 py-2">
					<Search className="h-4 w-4 shrink-0 text-muted-foreground" />
					<input
						ref={searchRef}
						type="text"
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						placeholder={t("picker.search")}
						className="min-h-6 flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
					/>
				</div>
			</div>

			{/* Grid */}
			<div className="overflow-y-auto p-4 sm:p-5">
				{filtered.length === 0 ? (
					<p className="py-8 text-center text-sm text-muted-foreground">{t("picker.noResults")}</p>
				) : (
					<div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
						{filtered.map((p) => {
							const inStock = isInStock(p);
							const blend = isBlend(p);
							const active = p.slug === currentSlug;
							return (
								<button
									key={p.slug}
									type="button"
									aria-current={active}
									onClick={() => {
										setSearch("");
										onPick(p.slug);
									}}
									className={[
										"flex min-h-16 flex-col items-start gap-0.5 rounded-xl border px-3 py-2.5 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1",
										active
											? "bg-primary/5 border-primary"
											: "hover:border-primary/40 hover:bg-card/80 border-border bg-card",
										!inStock ? "opacity-80" : "",
									].join(" ")}
								>
									<span className="flex w-full items-center gap-1.5">
										{inStock && <span aria-hidden className="h-1.5 w-1.5 shrink-0 rounded-full bg-success" />}
										<span className="truncate text-sm font-semibold text-foreground">
											{getPeptideName(p, loc)}
										</span>
									</span>
									<span className="truncate font-mono text-[11px] text-muted-foreground">
										{getPeptideCategory(p, loc)}
									</span>
									<span className="mt-1 flex flex-wrap gap-1">
										{inStock && (
											<span className="bg-success/10 rounded-full px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-success">
												{t("picker.inStock")}
											</span>
										)}
										{blend && (
											<span className="bg-warning/10 rounded-full px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-warning">
												{t("picker.blend")}
											</span>
										)}
										{!inStock && (
											<span className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
												{t("picker.onlyCalc")}
											</span>
										)}
									</span>
								</button>
							);
						})}
					</div>
				)}
			</div>
		</ToolDialog>
	);
}

export function PickerModal(props: PickerModalProps) {
	return <ModalContent {...props} />;
}
